import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, Input, signal, SimpleChanges } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { take } from 'rxjs';
import { PhoneCallButtonComponent } from '../../../basic/buttons/phone-call-button/phone-call-button.component';
import { PrimaryButtonComponent } from '../../../basic/buttons/primary-button/primary-button.component';
import { Resume } from '../../../core/resume/resume';
import { ResumeService } from '../../../core/resume/resume.service';
import { SessionTemplate } from '../../../core/session-template/session-template';
import { SessionTemplateService } from '../../../core/session-template/session.template.service';
import { SessionService } from '../../../core/session/session.service';
import { WorkspaceHolderService } from '../../../core/workspace/workspace.holder.service';
import { EditResumeComponent } from '../../resume/edit-resume/edit-resume.component';
import { NewEditSessionTemplateComponent } from '../new-edit-session-template/new-edit-session-template.component';

@Component({
  selector: 'app-session-template-card',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzButtonModule,
    NzIconModule,
    NzTagModule,
    NzBadgeModule,
    MatTooltipModule,

    NewEditSessionTemplateComponent,
    EditResumeComponent,
    PrimaryButtonComponent,
    PhoneCallButtonComponent,
  ],
  templateUrl: './session-template-card.component.html',
  styleUrl: './session-template-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SessionTemplateCardComponent {
  @Input({ required: true }) sessionTemplate?: SessionTemplate;

  @Input() editable = false;
  @Input() selectable = false;
  @Input() isSelected = false;

  resumeList = toSignal(this.resumeService.resumeList$(), { initialValue: [] });

  resume = signal<Resume | undefined>(undefined);

  isEditSessionTemplateVisible: boolean = false;

  constructor(
    private sessionService: SessionService,
    private resumeService: ResumeService,
    private sessionTemplateService: SessionTemplateService,
    private messageService: NzMessageService,
    private workspaceHolderService: WorkspaceHolderService,
    private router: Router
  ) {
    effect(
      () => {
        const tempResume = this.resumeList().find((c) => c.id === this.sessionTemplate?.unsafeSubData?.resumeId);
        this.resume.set(tempResume);
      },
      { allowSignalWrites: true }
    );
  }

  getClassList() {
    if (this.selectable && this.isSelected) {
      return ['selectable', 'is-selected'];
    } else if (this.selectable) {
      return ['selectable'];
    } else {
      return [];
    }
  }

  getSalesCycle(saleCycleId?: string) {
    return this.workspaceHolderService.currentWorkspace().settings?.salesCycleList?.find((c) => c.id === saleCycleId)
      ?.title;
  }

  resumeChanged = toSignal(this.resumeService.resumeChanged$, { initialValue: 0 });

  selectedPersonalityList = computed(() => {
    this.resumeChanged();

    return this.resumeService.getPersonalityList(this.resume())();
  });

  ngOnChanges(changes: SimpleChanges) {
    for (const propName in changes) {
      if (propName === 'sessionTemplate') {
        const chng = changes[propName];
        if (chng.previousValue !== chng.currentValue) {
          const sessionTemplate = chng.currentValue;

          const tempResume = this.resumeList().find((c) => c.id === sessionTemplate?.unsafeSubData?.resumeId);
          this.resume.set(tempResume);
        }
      }
    }
  }

  onEditClick() {
    this.isEditSessionTemplateVisible = true;
  }

  onCopyClick() {
    this.sessionTemplateService
      .add$(`Copied ${this.sessionTemplate!.title!}`, { ...this.sessionTemplate!.unsafeSubData } as any)
      .subscribe((data) => {
        if (data) {
          this.messageService.success('Copied!');
        }
      });
  }

  onStartCallClick(sessionTemplate: SessionTemplate, event: Event) {
    event.stopPropagation();

    this.sessionService
      .addSession$(sessionTemplate.id)
      .pipe(take(1))
      .subscribe((data) => {
        if (!data) {
          return;
        }
        this.sessionService
          .launchSession$(data)
          .pipe(take(1))
          .subscribe(() => {
            this.router.navigate(['', 'practice', data.id]);
          });
      });
  }
}
